import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import { getApiErrorMessage, getCityFromSearchParams } from "../../utils/consoleUtils";
import { ErrorHandler } from "../../utils/errorHandler";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import "../../styles/console.css";

const STATUS_KEYS = ["submitted", "review", "draft", "approved", "rejected"];
/** v18: "Needs review" for review status. */
const STATUS_LABELS = {
  submitted: "Submitted",
  review: "Needs review",
  draft: "Draft",
  approved: "Approved",
  rejected: "Rejected",
};
const TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "club", label: "Club submissions" },
  { value: "run", label: "Run suggestions" },
];

export default function SubmissionsView() {
  const [searchParams] = useSearchParams();
  const cityId = getCityFromSearchParams(searchParams);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [countsByStatus, setCountsByStatus] = useState(null);
  const [showStaleQueue, setShowStaleQueue] = useState(false);

  const { data: qualityData, error: qualityError } = useConsoleFetch(
    () => (cityId ? consoleApi.stats.qualityChecks(cityId) : Promise.resolve(null)),
    [cityId]
  );

  const listParams = { limit: 100 };
  if (cityId) listParams.city_id = cityId;
  if (typeFilter) listParams.type_filter = typeFilter;
  if (statusFilter) listParams.status_filter = statusFilter;

  const { data: submissionsRaw, loading, error } = useConsoleFetch(
    () => consoleApi.submissions.list(listParams).then((res) => (Array.isArray(res) ? res : [])),
    [cityId, typeFilter, statusFilter],
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    let cancelled = false;
    const params = cityId ? { city_id: cityId } : {};
    consoleApi.submissions
      .counts(params)
      .then((data) => {
        if (cancelled) return;
        const next = {};
        STATUS_KEYS.forEach((k) => {
          if (data != null && typeof data[k] === "number") next[k] = data[k];
        });
        setCountsByStatus(next);
      })
      .catch((e) => {
        if (!cancelled) {
          ErrorHandler.logError("SubmissionsView - counts", e, { cityId });
          setCountsByStatus(null);
        }
      });
    return () => { cancelled = true; };
  }, [cityId]);

  const submissions = submissionsRaw ?? [];

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetailError(null);
    consoleApi.submissions
      .get(selectedId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) {
          ErrorHandler.logError("SubmissionsView", e, { submissionId: selectedId });
          setDetailError(getApiErrorMessage(e));
        }
      });
    return () => { cancelled = true; };
  }, [selectedId]);

  const totalCount =
    countsByStatus != null && typeof countsByStatus.total === "number"
      ? countsByStatus.total
      : null;

  return (
    <ConsoleViewShell loading={loading} error={error} loadingLabel="Loading submissions…">
      <div className="console-main-inner console-sub-layout">
        <div style={{ gridColumn: "1 / -1" }}>
          <ConsolePageHeader
            label="City Seeding"
            title="Submissions"
            sub="Club and run submissions for review. Filter by type and status. All data from live API."
          />
        </div>
        <aside className="console-sub-sidebar">
          <div className="console-sub-sidebar-section">
            <div className="console-sub-sidebar-head">Type</div>
            <div className="console-sub-filter-group">
              <button
                type="button"
                className={`console-sub-filter-btn ${!typeFilter ? "active" : ""}`}
                onClick={() => setTypeFilter("")}
              >
                All
              </button>
              {TYPE_OPTIONS.filter((o) => o.value).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`console-sub-filter-btn ${typeFilter === opt.value ? "active" : ""}`}
                  onClick={() => setTypeFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="console-sub-sidebar-section">
            <div className="console-sub-sidebar-head">Status</div>
            <div className="console-sub-filter-group">
              <button
                type="button"
                className={`console-sub-filter-btn ${!statusFilter ? "active" : ""}`}
                onClick={() => setStatusFilter("")}
              >
                All {totalCount != null ? <span className="console-sub-filter-count">{totalCount}</span> : null}
              </button>
              {STATUS_KEYS.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`console-sub-filter-btn ${statusFilter === status ? "active" : ""}`}
                  onClick={() => setStatusFilter(status)}
                >
                  {STATUS_LABELS[status] ?? status}{" "}
                  {countsByStatus != null && countsByStatus[status] != null ? (
                    <span className="console-sub-filter-count">{countsByStatus[status]}</span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
          <div className="console-sub-sidebar-section console-sub-sidebar-actions">
            <button type="button" className="console-sub-btn-primary" disabled title="Paste an Instagram club URL to auto-generate draft records. Coming soon.">
              + Source Intake
            </button>
            <button type="button" className="console-sub-btn-secondary" disabled title="Create a submission manually. Coming soon.">
              + Manual Submission
            </button>
          </div>
          {cityId && qualityData && !qualityError && Array.isArray(qualityData.stale_data) && qualityData.stale_data.length > 0 && (
            <div className="console-sub-sidebar-section">
              <div className="console-sub-sidebar-head">
                Maintenance Queue
                <button
                  type="button"
                  onClick={() => setShowStaleQueue(!showStaleQueue)}
                  className="console-sub-toggle-btn"
                  style={{ marginLeft: "auto", fontSize: "10px" }}
                >
                  {showStaleQueue ? "Hide" : "Show"} ({qualityData.stale_data.length})
                </button>
              </div>
              {showStaleQueue && (
                <div className="stale-queue-list">
                  {qualityData.stale_data.slice(0, 10).map((item, i) => (
                    <div key={i} className="stale-queue-item">
                      <div className="stale-queue-type">{item.type?.replace(/_/g, " ")}</div>
                      <div className="stale-queue-name">{item.name}</div>
                      {item.days_since != null && (
                        <div className="stale-queue-meta">{item.days_since} days ago</div>
                      )}
                      <div className={`stale-queue-priority priority-${item.priority}`}>
                        {item.priority}
                      </div>
                    </div>
                  ))}
                  {qualityData.stale_data.length > 10 && (
                    <div className="stale-queue-more">
                      +{qualityData.stale_data.length - 10} more
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </aside>
        <div className="console-sub-list">
          <div className="console-sub-list-header">
            <div className="console-sub-list-title">Submissions</div>
            <div className="console-sub-list-count">
              {statusFilter || typeFilter
                ? `${submissions.length} of ${totalCount ?? "—"}`
                : submissions.length}
            </div>
          </div>
          <ul className="console-sub-list-items">
            {submissions.map((s) => (
              <li
                key={s.id}
                onClick={() => setSelectedId(s.id)}
                className={`console-sub-list-item ${selectedId === s.id ? "selected" : ""}`}
              >
                <div className="console-sub-item-title">{s.name || s.type || s.id}</div>
                <div className="console-sub-item-meta">{s.status} · {s.type}</div>
              </li>
            ))}
          </ul>
          {submissions.length === 0 && (
            <p className="console-sub-list-empty">No submissions.</p>
          )}
        </div>
        <div className="console-sub-panel">
          {detailError && <p className="console-sub-panel-error">{detailError}</p>}
          {!detailError && detail && (
            <div className="console-sub-panel-detail">
              <div className="console-sub-panel-row"><span className="console-sub-panel-label">Type</span> {detail.type}</div>
              <div className="console-sub-panel-row"><span className="console-sub-panel-label">Status</span> {detail.status}</div>
              {detail.name && <div className="console-sub-panel-row"><span className="console-sub-panel-label">Name</span> {detail.name}</div>}
              {detail.city && <div className="console-sub-panel-row"><span className="console-sub-panel-label">City</span> {detail.city}</div>}
              {detail.notes && <div className="console-sub-panel-row"><span className="console-sub-panel-label">Notes</span> {detail.notes}</div>}
            </div>
          )}
          {!detailError && !detail && selectedId && <p className="console-sub-panel-muted">Loading…</p>}
          {!detailError && !detail && !selectedId && (
            <div className="console-sub-panel-empty">
              <div className="console-sub-panel-empty-icon">↑</div>
              <div>Select a submission to review</div>
            </div>
          )}
        </div>
      </div>
    </ConsoleViewShell>
  );
}
